import styles from './styles.scss';
import cn from 'classnames';
import Portal, { PortalProps } from 'components/Shared/Portal';
import { Component, createRef, useEffect, memo } from 'react';
import TransitionGroup from 'react-transition-group-plus';
import { Overlay } from 'components/Shared/Overlay/Overlay';
import { getScrollbarWidth, withCurrentPage } from 'utils';
import headerStyles from 'components/Header/themes/BGH/styles.scss';
import layoutStyles from 'components/Pagelayout/styles.scss';
import { useMobile } from '../responsive';
import { useSelector } from 'react-redux';
import { getBrandContentDisplayType } from 'views/ProductlistPage/selectors';
import { BrandContentDisplayType } from 'models/BrandContentDisplayType';

export { RedDrawer } from './redDrawer';

const ANIMATION_DELAY = 200;
type Side = 'left' | 'right' | 'bottom|right' | 'bottom|left' | 'bottom';
type Width = 'default' | 'full';
type AppearanceTypes = Side | 'default' | 'header';
type PaddingType = 'none' | 'normal';

interface OwnState {
  className: string;
}
export interface OwnProps extends DrawerProps, Partial<Pick<PortalProps, 'container'>> {
  isOpen: boolean;
  transparent?: true;
  isMobile?: boolean;
}

// TODO: Having an overlay and a drawer in a portal messes up SSR
// Not sure how we should fix this but the drawer will have to live inside
// The react tree for now
export default ({
  isOpen,
  children,
  transparent,
  side,
  width,
  appearance,
  className,
  onClose,
  padding,
  setFixedOnBody = true,
  container
}: OwnProps) => {
  const isMobile = useMobile();

  return (
    <Portal container={container}>
      <TransitionGroup>
        {isOpen && <Overlay onClick={onClose} transparent={transparent} />}
        {isOpen && (
          <Drawer
            {...{
              side,
              width,
              appearance,
              className,
              onClose,
              padding,
              setFixedOnBody,
              isMobile
            }}
          >
            {children}
          </Drawer>
        )}
      </TransitionGroup>
    </Portal>
  );
};

interface DrawerProps {
  onClose?: (e: React.MouseEvent<HTMLDivElement>) => void;
  padding?: PaddingType;
  children?: React.ReactNode;
  className?: string;
  appearance?: AppearanceTypes;
  width?: Width;
  side?: Side;
  setFixedOnBody?: boolean;
  isMobile?: boolean;
  displayType?: BrandContentDisplayType;
}

// TODO: is there a better solution for this?
let numberOfOpenDrawers = 0;

type CloseMenuProps = {
  displayType?: BrandContentDisplayType;
};

const closeMenu = ({ displayType }: CloseMenuProps) => {
  numberOfOpenDrawers = Math.max(numberOfOpenDrawers - 1, 0);

  if (numberOfOpenDrawers > 0) {
    // There are still drawers open after closing this one so don't mess with body styles now.
    return;
  }

  const tobparElem = document.querySelector<HTMLElement>(`.${headerStyles.topBar}`);

  document.body.classList.remove(layoutStyles.lockBody);
  document.body.style.marginRight = `0px`;
  document.body.style.overflowY = ``;
  document.body.style.width = '';
  document.body.style.maxWidth = `none`;

  const scrollY = document.body.style.top;
  document.body.style.top = '';
  document.body.style.position = '';

  // to not scroll to top, on pages with ?clean=2 in URL
  if (displayType !== BrandContentDisplayType.ScrollPast) {
    window.scrollTo(0, parseInt(scrollY || '0', 0) * -1);
  }

  if (tobparElem !== null) {
    tobparElem.style.marginRight = `0px`;
  }
};

const openMenu = () => {
  numberOfOpenDrawers++;

  const scrollbarWidth = getScrollbarWidth();
  const tobparElem = document.querySelector<HTMLElement>(`.${headerStyles.topBar}`);

  document.body.classList.add(layoutStyles.lockBody);
  document.body.style.overflowY = `hidden`;
  document.body.style.width = '100%';
  if (document.body.style.position !== 'fixed') {
    document.body.style.maxWidth = `calc(100% - ${scrollbarWidth}px)`;
    document.body.style.marginRight = `${scrollbarWidth}px`;
  }

  requestAnimationFrame(() => {
    if (!document.body.style.top) {
      document.body.style.top = `-${window.scrollY || window.pageYOffset}px`;
    }
    document.body.style.position = 'fixed';
  });

  if (tobparElem !== null) {
    tobparElem.style.marginRight = `${scrollbarWidth}px`;
  }
};

interface MenuDrawerProps extends OwnProps {
  numberOfPanels: number;
}

export const MenuDrawer = memo(
  ({
    isOpen,
    children,
    appearance,
    className,
    width,
    side,
    transparent,
    onClose
  }: MenuDrawerProps) => {
    const sideOrientation = side || appearance;
    const displayType = useSelector(withCurrentPage(getBrandContentDisplayType));

    useEffect(() => {
      if (!isOpen) {
        setTimeout(() => closeMenu({ displayType }), ANIMATION_DELAY);
      } else {
        openMenu();
      }
    }, [displayType, isOpen]);

    return (
      <>
        {isOpen && <Overlay onClick={onClose} transparent={transparent} />}
        <div
          tabIndex={-1}
          className={cn(styles.drawer, styles.noPadding, className, {
            [styles.exit]: !isOpen,
            [styles.enter]: isOpen,
            [styles.menuRight]: sideOrientation === 'right',
            [styles.full]: width === 'full'
          })}
        >
          {children}
        </div>
      </>
    );
  },
  function arePropsEqual(prev, next) {
    return prev.isOpen === next.isOpen && prev.numberOfPanels === next.numberOfPanels;
  }
);

interface IsMobileSideProps {
  position: Side;
}

class Drawer extends Component<DrawerProps, OwnState> {
  public state = {
    className: ''
  };

  private timeout: number;
  private ref = createRef<HTMLDivElement>();

  public componentDidMount() {
    openMenu();
  }

  public componentWillUnmount() {
    closeMenu({ displayType: this.props.displayType });
    window.clearTimeout(this.timeout);
  }

  public componentWillEnter(cb: () => void) {
    this.ref.current && !this.hasFocus() && this.ref.current.focus();
    this.setState(
      () => ({ className: styles.enter }),
      () => window.requestAnimationFrame(cb)
    );
  }

  public componentWillLeave(cb: () => void) {
    this.setState(
      () => ({ className: styles.exit }),
      () => (this.timeout = window.setTimeout(cb, ANIMATION_DELAY))
    );
  }

  public render() {
    const {
      props: { appearance, padding, children, className, width }
    } = this;
    const side = this.props.side || this.props.appearance;
    const style = this.getBottomStyles();
    return (
      <div
        style={style}
        tabIndex={-1}
        ref={this.ref}
        className={cn(styles.drawer, this.state.className, className, {
          [styles.bottom]: this.props.isMobile && this.getIsMobileLayout({ position: 'bottom' }),
          [styles.left]: side === 'left' || this.getIsMobileLayout({ position: 'left' }),
          [styles.right]: side === 'right' || this.getIsMobileLayout({ position: 'right' }),
          [styles.noPadding]: padding === 'none',
          [styles.normalPadding]: padding === 'normal',
          [styles.header]: appearance === 'header',
          [styles.full]: width === 'full' && !this.getIsMobileLayout({ position: 'bottom' })
        })}
      >
        {children}
      </div>
    );
  }

  private getIsMobileLayout = ({ position }: IsMobileSideProps): boolean => {
    const { side } = this.props;
    const containsBottom = side?.includes('bottom' as Side);
    const hasSide = side?.includes(position);
    return !!containsBottom && !!hasSide;
  };

  private getBottomStyles = (): { [style: string]: string | number } => {
    if (!this.props.isMobile || !this.getIsMobileLayout({ position: 'bottom' })) {
      return {};
    }

    const height = this.getHeightOfChildren();
    return { height, top: `Calc(100% - ${height}px)` };
  };

  private getHeightOfChildren = (): number => {
    const children = Array.from(this.ref.current?.children || []);
    return children.reduce((height, child) => height + child.scrollHeight, 0);
  };

  private hasFocus = () =>
    this.ref.current !== null &&
    (document.activeElement === this.ref.current ||
      this.ref.current.contains(document.activeElement));
}
